import { Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { Close } from '@mui/icons-material';
import { Chip, InputAdornment, Select as MuiSelect, Typography } from '@mui/material';
import { colorTheme, extraColor } from '@styles/muiTheme';
import { ReactComponent as IconDocument } from '@svg/icon-document.svg';

export const PickerBorder = styled.div`
  flex: 1;
  height: 62px;
  display: flex;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.3em;
  border: 1px dashed ${colorTheme.palette.grey[700]};

  &:hover {
    background-color: ${colorTheme.palette.secondary.light};
    cursor: pointer;
  }
`;

export const PickerLinkLikeText = styled.span`
  color: ${colorTheme.palette.primary.light};
  cursor: pointer;
`;

export const PickerIconWrapper = styled.div`
  margin-right: 10px;

  svg {
    height: 40px;
    width: 40px;
  }
`;

export const PickerDocumentContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: row-reverse;
  margin: -10px 0 15px;
`;

export const PickerFileSpecificationMessage = styled.div<{ error?: boolean }>`
  && {
    color: ${({ error }) => (error ? colorTheme.palette.error.main : 'inherit')};
    width: 190px;
    max-width: 100%;
    margin-top: 15px;
    line-height: 1.3;
    font-size: 13px;
    text-align: center;

    ${({ theme: { breakpoints } }) => breakpoints.up('md')} {
      width: auto;
    }
  }
`;

export const DocumentPreviewWrapper = styled.div`
  flex: 1;
  display: flex;
  border-radius: 8px;
  align-items: center;
  border: solid 1px ${colorTheme.palette.grey[400]};
  background-color: ${colorTheme.palette.secondary.light};
  padding: 10px 0 10px 10px;
`;

export const DocumentPreviewThumbnail = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 76px);
  padding-left: 10px;
`;

export const DocumentPreviewFileName = styled.div`
  font-size: 14px;
  padding-bottom: 5px;
  color: ${colorTheme.palette.common.black};
  overflow: hidden;
  word-break: break-all;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DocumentPreviewFileDetails = styled.div`
  font-size: 10px;
  line-height: 14px;
  color: ${colorTheme.palette.common.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DocumentPreviewIcon = styled(IconDocument)`
  width: 35px;
  height: 40px;
`;

export const DocumentPreviewIconRemove = styled(Close)`
  && {
    color: ${({ theme }) => theme.palette.common.white};
    font-size: 32px;
    background-color: ${colorTheme.palette.grey[700]};
    font-size: 18px;
    padding: 3px;
    border-radius: 100%;
    cursor: pointer;
  }
`;


export const DatePickerWrapper = styled.div<{ isFocused: boolean; error?: boolean }>`
  .MuiTextField-root {
    min-width: 1px;
  }

  .MuiStack-root {
    padding: 0;
  }

  .MuiTextField-root {
    min-width: 50% !important;
  }

  fieldset {
    border-bottom: ${({ error, isFocused }) =>
      !error && isFocused ? colorTheme.palette.primary.light : error ? `1px solid ${colorTheme.palette.error.main}` : colorTheme.palette.grey[500]} !important;
  }
`;

export const PdfViewerWrapper = styled.div`
  display: flex;
  height: calc(100% - 80px);

  .rpv-open__input-wrapper {
    display: none;
  }
`;

export const DraggableListWrapper = styled.div<{ showScroll?: boolean }>`
  height: 100%;
  overflow-x: hidden;
  overflow-y: ${({ showScroll }) => (showScroll ? 'auto' : 'hidden')};

  /* Styling the scrollbar of DraggableListWrapper */   
  &::-webkit-scrollbar {
    width: 0.4em; /* Adjust width for the vertical scrollbar */
  }

  &::-webkit-scrollbar-track {
    background: ${extraColor.palette.grey.light}; 
    box-shadow: inset 0 0 6px ${extraColor.palette.grey.light};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${extraColor.palette.grey.main}; 
    border-radius: 4px;
  }
`;

export const DraggableListScrollDroppable = styled(Droppable)`
  height: 100%;
`;

export const ColorPickerWrapper = styled.div`
  position: relative;
`;

export const ColorPickerContainer = styled.div`
  z-index: 2;
  position: fixed;
  border-radius: 11px;
  border: ${({ theme }) => `3px solid ${theme.palette.secondary.light}`};
`;

export const ColorPickerCircle = styled.div<{ color: string }>`
  width: 25px;
  height: 25px;
  border: ${({ theme }) => `3px solid ${theme.palette.common.white}`};
  border-radius: 100%;
  background-color: ${({ color }) => color};
`;

export const ColorPickerFieldLabel = styled(Typography).withConfig<{ wrap?: boolean }>({ shouldForwardProp: (prop) => prop !== 'wrap' })(
  ({ wrap }) => `
  top: 3px;
  left: 10px;
  z-index: 1;
  color: ${colorTheme.palette.common.black};
  font-size: 11px;
  font-weight: 700;
  white-space: ${wrap ? 'wrap' : 'nowrap'};
  margin-right: 5px;
  cursor: default;
`,
);

export const FileDropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  border-width: 2px;
  border-radius: 5px;
  border-color: ${colorTheme.palette.secondary.main};
  border-style: dashed;
  color: ${colorTheme.palette.common.black};
  outline: none;
  transition: border 0.24s ease-in-out;
  text-align: center;
  font: normal normal bold 18px/33px Open Sans;
  letter-spacing: 0px;
`;

export const FileDropzoneLabel = styled(Typography)({
  color: colorTheme.palette.common.black,
  fontSize: '18px',
  fontWeight: 'bold',
  display: 'block',
  marginTop: '0px !important',
  lineHeight: '30px',
});

export const InputLabel = styled.div<{ top?: string; left?: string; isShrunk?: boolean }>`
  top: ${({ top, isShrunk }) => isShrunk ? '-6px' : top ?? '0'};
  left: ${({ left }) => left ?? '10px'};
  z-index: 1;
  color: ${colorTheme.palette.common.black};
  font-size: 11px;
  font-weight: 600;
  position: absolute;
  white-space: nowrap;
`;

export const StyledSelect = styled(MuiSelect).withConfig<{ dense?: boolean }>({
  shouldForwardProp: (prop) => prop !== 'dense',
})(
  ({ dense }) => `
  width: 100%;
  height: 100%;
  background: ${colorTheme.palette.common.white}
  border-radius: 8px;
  
  ${
    dense &&
    `
    min-height: 36px;
    
    .MuiSelect-select {
      padding-top: 0;
      padding-bottom: 0;
    }
  `
  };
`,
);

export const SelectWrapper = styled.div<{ labeled?: boolean }>(
  ({ labeled }) => `
  width: 100%;
  position: relative;

  ${
    labeled &&
    `
    .MuiInputBase-root {
      border-radius: 0;
    }
    
    .MuiSelect-select {
      height: 20px;
      padding: 18px 10px 2px;
      
      em {
        font-style: normal;
      }
    }
  `
  }
`,
);

export const TextInputWrapper = styled.div<{ withLabel: boolean }>(
  ({ theme, withLabel }) => `
  position: relative;
  
  .MuiFormControl {
    &-root {
      width: 100%;
    }
  }

  .MuiInputBase-adornedStart, MuiInputBase-adornedEnd {
    padding-rigth: 0;
    padding-left: 0;
  }
  
  .MuiInputBase {
    &-root {
      width: 100%;
      border-radius: 0;
      
      &:not(.Mui-disabled) {
        background: ${theme.palette.common.white};
      }
    }
    
    &-input {
      font-size: 12px;
      font-weight: 600;
	    height:0.9em;
      padding: ${ withLabel ? '20px 10px 8px' : '8px 5px 8px 10px' };
      color: ${ theme.palette.primary.main };
    }
    
    &-inputMultiline {
      font-weight: 400;
      line-height: 19px;
      padding: 5px 10px;
      color: ${theme.palette.common.black};
    }
  }
  
  .MuiInputAdornment-root {
    margin: 0;
  }
`,
);

export const StyledSelectWrapper = styled(SelectWrapper)`
  overflow: hidden;
`;

export const StyledNumberInputWrapper = styled(TextInputWrapper)`
  overflow: hidden;
`;

export const InputAdornmentWrapper = styled(InputAdornment)<{ $isAbleToCall: boolean }>`
  &:hover {
    cursor: ${({ $isAbleToCall }) => $isAbleToCall && 'pointer'};
  }
`;

export const CreditCardSecret = styled.div`
  position: absolute;
  z-index: 1;
  top: 18px;
  color: ${colorTheme.palette.primary.main};
  font-size: 13px;
  left: 5px;
  height: 20px;
  background-color: ${colorTheme.palette.common.white};
`;

export const TextFieldWrapper = styled.div<{ highlightFullBorder?: boolean }>(
  ({ highlightFullBorder }) => `

  ${
    highlightFullBorder &&
    `
    .MuiInputBase-root {
      border: 1px solid ${colorTheme.palette.error.main};
    }
  `
  }
`,
);

export const FileDropzoneChip = styled(Chip)`
  background-color:${colorTheme.palette.primary.light};
  color: ${colorTheme.palette.common.white};
  border-radius: 8px;
  font-weight: bold;
  font-size: 14px;
  max-width: 150px;
  margin: 0 auto !important;
  margin-top: 15px !important;
`;

export const AutoCompleteWrapper = styled.div<{
  labeled?: boolean,
  backgroundColor?: string,
  multiple?: boolean,
  margin?: string,
  removeExtraPadding?: boolean,
  placeholderOpacity?: number,
  highlightFullBorder?: boolean,
}>(
  ({ labeled, margin, multiple, backgroundColor, removeExtraPadding, placeholderOpacity, highlightFullBorder }) => `
  width: 100%;
  position: relative;

	${placeholderOpacity && `
	.MuiAutocomplete-input::-webkit-input-placeholder {
		opacity: ${placeholderOpacity || '0.5'}
	}
	`}

	.MuiInputBase-root {
		background-color: ${backgroundColor || 'white'} ;
	}

	div.MuiOutlinedInput-root {
      padding: 9px 0px 0px 5px;  
    }

	${removeExtraPadding && `
		div.MuiOutlinedInput-root {
			padding: inherit;
		}
		div.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root{
			padding-right: 55px;
		}
	`}

	${multiple && margin && `
	.MuiInputBase-root {
		margin: ${margin};
	}`
	}

	${
	labeled &&
	`
	.MuiInputBase-root {
		border-radius: 0;
	}

	.MuiInputBase-input  {
		height: 17px !important;
	}

	.MuiOutlinedInput-root .MuiAutocomplete-input {
		font-size: 12px;
		font-weight: 600;
		padding: 5px 1px 0px;
		color: rgb(11, 81, 121);
	}

    .MuiSelect-select {
      height: 28px;
      padding: 18px 10px 2px;
      
      em {
        font-style: normal;
      }
    }
  `
  }

  ${
    highlightFullBorder &&
    `
    .MuiInputBase-root {
      border: 1px solid ${colorTheme.palette.error.main} !important;
    }
  `
  }
`,
);

export const GroupHeader = styled.div`
  position: sticky;
  top: -8px;
  padding: 4px 10px;
  color: #fff;
  background-color: rgb(11, 81, 121);
  text-align: end;
  text-transform: uppercase;
`;

export const CustomMessageWrapper = styled.div<{ 
  color: string, 
  textTransform?: string, 
  textAlign?: string }>(({ color,  textTransform, textAlign}) => `
    color: ${color ? color : colorTheme.palette.error.main};
    text-transform: ${textTransform ? textTransform : 'none'};
    text-align: ${textAlign ? textAlign : 'left'};;
    font-weight: bold;
    margin-top: 5px;
`);
